import { PAGINATION_DEFAULTS } from '../api'
import { useDashboardApi } from '@/plugins/app@lz/dashboard/_services/dashboard.service'

import { useGetPatients, DEFAULT_FILTERS as PATIENTS_FILTERS } from '@/plugins/app@lz/patient/_services/patients.service'
import { useGetRequests, DEFAULT_FILTERS as DRAFT_REQUESTS_FILTERS } from '@/plugins/app@lz/request/_services/requests.service'
import { useGetLisRequests, DEFAULT_FILTERS as LIS_REQUESTS_FILTERS } from '@/plugins/app@lz/request/_services/lis-requests.service'
import { useGetResults, DEFAULT_FILTERS as RESULTS_FILTERS } from '@/plugins/app@lz/result/_services/results.service'
import { useGetMaterialOrders, DEFAULT_FILTERS as MATERIAL_ORDERS_FILTERS } from '@/plugins/app@lz/material-order/_services/material-order.service'
import { useGetReservations, DEFAULT_FILTERS as RESERVATIONS_FILTERS } from '@/plugins/app@oc/reservation/_services/reservations.service'
import { useGetLisPatients, DEFAULT_FILTERS as LIST_PATIENTS_FILTERS } from '@/plugins/app@oc/patient/_services/lis-patients.service'

import { useAuthStore } from '@/plugins/w/auth/store/auth.store'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '../_config/store'
import { useQueryClient } from '@tanstack/vue-query'
import { format, subYears, addYears } from 'date-fns'

export const usePrefetch = () => {
	const route = useRoute()
	const authStore = useAuthStore()
	const queryClient = useQueryClient()
	const appStore = useAppStore()

	const materialOrdersState = route.name == 'Materials Orders' && route.query.state ? route.query.state : MATERIAL_ORDERS_FILTERS.state
	let resultAccessIds = []

	try {
		resultAccessIds = JSON.parse(localStorage.getItem(`results_accesses_user_${authStore.getUser?.id}`) || '[]')

		if (!resultAccessIds.length && authStore.getCurrentAccess?.id) {
			resultAccessIds.push(authStore.getCurrentAccess?.id)
		}
	} catch (error) {
		if (authStore.getCurrentAccess?.id) {
			resultAccessIds = [authStore.getCurrentAccess?.id]
		}
	}

	const getOcPrefetchQueries = () => {
		const { PREFETCH_QUERY: TODAY_RESERVATIONS_PREFETCH_QUERY } = useGetReservations(ref(PAGINATION_DEFAULTS), ref({ ...RESERVATIONS_FILTERS, date_from: format(new Date(), 'yyyy-MM-dd'), date_to: format(new Date(), 'yyyy-MM-dd'), finished: false }))
		const { PREFETCH_QUERY: ALL_RESERVATIONS_PREFETCH_QUERY } = useGetReservations(ref(PAGINATION_DEFAULTS), ref({ ...RESERVATIONS_FILTERS, date_from: format(subYears(new Date(), 10), 'yyyy-MM-dd'), date_to: format(addYears(new Date(), 10), 'yyyy-MM-dd'), finished: null }))
		const { PREFETCH_QUERY: COMPLETED_RESERVATIONS_PREFETCH_QUERY } = useGetReservations(ref(PAGINATION_DEFAULTS), ref({ ...RESERVATIONS_FILTERS, date_from: format(subYears(new Date(), 10), 'yyyy-MM-dd'), date_to: format(addYears(new Date(), 10), 'yyyy-MM-dd'), finished: null }))
		const { PREFETCH_QUERY: LIS_PATIENTS_PREFETCH_QUERY } = useGetLisPatients(ref(PAGINATION_DEFAULTS), ref(LIST_PATIENTS_FILTERS))

		let current: any

		switch (route.name) {
			case 'Reservations':
				const state = route.query.tab ? route.query.tab : 'today'
				switch (state) {
					case 'today':
						current = TODAY_RESERVATIONS_PREFETCH_QUERY
						break
					case 'all':
						current = ALL_RESERVATIONS_PREFETCH_QUERY
						break
					case 'completed':
						current = COMPLETED_RESERVATIONS_PREFETCH_QUERY
						break
				}
				break
			case 'Patients':
				current = LIS_PATIENTS_PREFETCH_QUERY
				break
		}

		const allQueries = [TODAY_RESERVATIONS_PREFETCH_QUERY, ALL_RESERVATIONS_PREFETCH_QUERY, COMPLETED_RESERVATIONS_PREFETCH_QUERY, LIS_PATIENTS_PREFETCH_QUERY]
		const others = allQueries.filter((query) => query != current)

		return {
			current,
			others
		}
	}

	const getLzPrefetchQueries = () => {
		const { PREFETCH_QUERY: DASHBOARD_PREFETCH_QUERY } = useDashboardApi()
		const { PREFETCH_QUERY: PATIENTS_PREFETCH_QUERY } = useGetPatients(ref(PAGINATION_DEFAULTS), ref(PATIENTS_FILTERS))
		const { PREFETCH_QUERY: REQUESTS_DRAFT_PREFETCH_QUERY } = useGetRequests(ref(PAGINATION_DEFAULTS), ref({ ...DRAFT_REQUESTS_FILTERS }))
		const { PREFETCH_QUERY: REQUESTS_DRAFT_ONLINE_PAYMENT_PREFETCH_QUERY } = useGetRequests(ref(PAGINATION_DEFAULTS), ref({ ...DRAFT_REQUESTS_FILTERS, payment_gate: ['ONLINE_PAYMENT'] }))
		const { PREFETCH_QUERY: LIS_REQUESTS_PREFETCH_QUERY } = useGetLisRequests(ref(PAGINATION_DEFAULTS), ref({ ...LIS_REQUESTS_FILTERS }))
		const { PREFETCH_QUERY: RESULTS_PREFETCH_QUERY } = useGetResults(ref(PAGINATION_DEFAULTS), ref({ ...RESULTS_FILTERS, accesses: resultAccessIds }))
		const { PREFETCH_QUERY: UNREAD_RESULTS_PREFETCH_QUERY } = useGetResults(ref(PAGINATION_DEFAULTS), ref({ ...RESULTS_FILTERS, accesses: resultAccessIds, readed: false }))
		const { PREFETCH_QUERY: MATERIALS_ORDERS_PREFETCH_QUERY } = useGetMaterialOrders(ref(PAGINATION_DEFAULTS), ref({ ...MATERIAL_ORDERS_FILTERS, state: materialOrdersState }))

		let current: any

		switch (route.name) {
			case 'Dashboard':
				current = DASHBOARD_PREFETCH_QUERY
				break
			case 'Patients':
				current = PATIENTS_PREFETCH_QUERY
				break
			case 'Requests':
				const requestState = route.query.state ? route.query.state : 'draft'

				if (requestState == 'draft') {
					current = REQUESTS_DRAFT_PREFETCH_QUERY
				} else if (requestState == 'draft-online') {
					current = REQUESTS_DRAFT_ONLINE_PAYMENT_PREFETCH_QUERY
				} else if (requestState == 'sent') {
					current = LIS_REQUESTS_PREFETCH_QUERY
				}

				break
			case 'Results':
				const resultState = route.query.state == route.query.state ? route.query.state : 'unread'
				current = resultState == 'unread' ? UNREAD_RESULTS_PREFETCH_QUERY : RESULTS_PREFETCH_QUERY
				break
			case 'Materials Orders':
				current = MATERIALS_ORDERS_PREFETCH_QUERY
				break
			default:
				current = null
		}

		// Removed for optimization: LIS_REQUESTS_PREFETCH_QUERY, UNREAD_RESULTS_PREFETCH_QUERY, RESULTS_PREFETCH_QUERY
		const allQueries = [DASHBOARD_PREFETCH_QUERY, PATIENTS_PREFETCH_QUERY, REQUESTS_DRAFT_PREFETCH_QUERY, MATERIALS_ORDERS_PREFETCH_QUERY]

		if (authStore.getCurrentAccess?.whitelist_can_use_online_payment) {
			allQueries.push(REQUESTS_DRAFT_ONLINE_PAYMENT_PREFETCH_QUERY)
		}

		const others = allQueries.filter((query) => query != current)

		return {
			current,
			others
		}
	}

	const prefetchFirst = async () => {
		const { current } = authStore.getUserType == 'OC' ? getOcPrefetchQueries() : getLzPrefetchQueries()
		if (current) await queryClient.prefetchQuery(current)
	}

	const prefetchAll = async (skipCurrent = false) => {
		const { current, others } = authStore.getUserType == 'OC' ? getOcPrefetchQueries() : getLzPrefetchQueries()

		const promises = []

		if (!skipCurrent && current) {
			promises.push(queryClient.prefetchQuery(current))
		}

		others.map((query) => queryClient.prefetchQuery(query))
		await Promise.all(promises)

		appStore.initializeInsuranceCompanies()
		appStore.initializeCountries()
		authStore.userInfo()
	}

	return {
		prefetchFirst,
		prefetchAll
	}
}
